import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { LanguageToggle } from "../components/LanguageToggle";
import { logout } from "../services/sessionAuth";

const DRAWER = 252;

function selected(pathname: string, to: string) {
  if (to === "/tickets") {
    return pathname === "/tickets" || (pathname.startsWith("/tickets/") && !pathname.startsWith("/tickets/new"));
  }
  if (to === "/home") return pathname === "/home";
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function DashboardLayout({
  children,
  superAdmin,
}: {
  children: ReactNode;
  superAdmin: boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const items = superAdmin
    ? [
        { to: "/home", label: t("nav.home"), icon: <HomeOutlinedIcon /> },
        { to: "/admin/dashboard", label: t("nav.dashboard"), icon: <DashboardOutlinedIcon /> },
        { to: "/admin/deployment", label: t("nav.deployment"), icon: <RocketLaunchOutlinedIcon /> },
        { to: "/tickets", label: t("nav.tickets"), icon: <ConfirmationNumberOutlinedIcon /> },
        { to: "/tickets/new", label: t("nav.new"), icon: <AddCircleOutlineIcon /> },
        { to: "/release-notes", label: t("nav.releaseNotes"), icon: <NewReleasesOutlinedIcon /> },
      ]
    : [
        { to: "/home", label: t("nav.home"), icon: <HomeOutlinedIcon /> },
        { to: "/tickets", label: t("nav.tickets"), icon: <ConfirmationNumberOutlinedIcon /> },
        { to: "/tickets/new", label: t("nav.new"), icon: <AddCircleOutlineIcon /> },
        { to: "/release-notes", label: t("nav.releaseNotes"), icon: <NewReleasesOutlinedIcon /> },
      ];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar position="fixed" color="inherit" sx={{ zIndex: (th) => th.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "grid",
              placeItems: "center",
            }}
          >
            <SupportAgentIcon fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {t("app.title")}
          </Typography>
          <LanguageToggle />
          <Button
            color="inherit"
            startIcon={<LogoutRoundedIcon />}
            onClick={() => void logout().then(() => window.location.assign("/login"))}
          >
            {t("app.signOut")}
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER,
          [`& .MuiDrawer-paper`]: { width: DRAWER, boxSizing: "border-box", pt: 9, px: 0.5 },
        }}
      >
        <List>
          {items.map((item) => (
            <ListItemButton key={item.to} component={Link} to={item.to} selected={selected(location.pathname, item.to)}>
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
      <Box sx={{ flexGrow: 1, pt: 10, minHeight: "100vh" }}>{children}</Box>
    </Box>
  );
}
